import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Award, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/34182370/pexels-photo-34182370.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative isolate overflow-hidden pt-[188px]">
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Agricultural drone spraying avocado groves and crop fields across South Florida — AeroAcres FAA Part 137 certified"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-primary-foreground">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-xs font-semibold tracking-wide border border-white/20"
              data-testid="hero-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              FAA Part 137 · Serving Homestead & Miami-Dade
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight"
              data-testid="hero-headline"
            >
              Drone crop spraying<br />
              for <span className="text-accent">South Florida</span> growers.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 max-w-xl text-base md:text-lg text-primary-foreground/85 leading-relaxed"
            >
              Precision pesticide, fungicide, fertilizer and seeding application by FAA-certified pilots.
              Cover more acres in less time — with less chemical, zero soil compaction, and crop-safe drift control.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <Button asChild data-testid="hero-quote-btn" className="rounded-full bg-accent hover:bg-accent/90 h-12 px-7 text-base font-semibold">
                <a href="#contact">Get a Free Quote <ArrowRight className="h-4 w-4 ml-1.5" /></a>
              </Button>
              <Button asChild variant="outline" data-testid="hero-call-btn" className="rounded-full bg-white/10 backdrop-blur border-white/30 hover:bg-white/20 text-white h-12 px-7 text-base font-semibold">
                <a href={`tel:${BUSINESS.phoneRaw}`} data-conversion="phone-call" className="track-phone-click"><Phone className="h-4 w-4 mr-1.5" /> {BUSINESS.phone}</a>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-12 flex flex-wrap items-center gap-6 text-sm text-primary-foreground/80"
            >
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> FAA Part 137 Certified</div>
              <div className="flex items-center gap-2"><Award className="h-4 w-4 text-accent" /> Licensed Applicators</div>
              <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-accent" /> EPA-Compliant Chemicals</div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: "30×", l: "Faster than ground sprayers" },
                { v: "20%", l: "Less chemical, same coverage" },
                { v: "0", l: "Soil compaction or rutting" },
                { v: "2,000+", l: "South FL acres serviced" },
              ].map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`} className="rounded-2xl p-5 glass border border-white/30 text-primary">
                  <div className="font-display text-3xl md:text-4xl font-bold">{s.v}</div>
                  <div className="text-xs md:text-sm text-primary/70 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Soft bottom fade to background */}
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-background pointer-events-none" />
    </section>
  );
}
